import type { LiveVehicle } from './types';

/**
 * Read a palette value out of the token layer at runtime — INLINED into the
 * package (slice 84) from web/src/lib/themeToken so this module is framework-free
 * and both apps resolve marker colours the same way. MapLibre paint and canvas
 * drawing take colour as a VALUE, not a CSS class, so these call sites read the
 * custom property; styles.css stays the single place a palette is defined.
 *
 * 🔴 PASS THE `--raad-*` PALETTE NAME, NOT THE `--color-raad-*` TAILWIND ALIAS —
 * the alias lives in an `@theme inline` block and resolves to an empty string.
 * The fallback is the value that must render when there is no document at all
 * (unit tests, any pre-hydration path) — never a placeholder.
 */
export function readToken(name: `--raad-${string}`, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Motion vocabulary and the marker's visual tone — pure, so the rules that
 * matter most are unit-testable without a WebGL context.
 *
 * The motion colours are design tokens, exact (PLAN §Markers):
 *   moving  — solid green  #12B76A   (chevron, rotated to course)
 *   idle    — solid amber  #F7B84B   (dot)
 *   stopped — solid slate  #5C6B7A   (dot; parked-but-live, "fresh fix, not
 *             moving" — must read differently from offline)
 *   unknown — SOLID violet #A98AD8   (dot; tracker in contact but the fix is too
 *             old to say what it's DOING. Its OWN token — not slate, not grey —
 *             because "the whole point is that they're different states". Solid,
 *             like the other live states: the tracker IS alive; only the fix is
 *             stale. Distinct violet hue reads apart from both greys at a glance,
 *             and is NOT the admin indigo.)
 *   offline — grey  #9AA7B4   (🔴 the MAP marker was hollow until 21 Sep and the
 *             live/not-live distinction used to be hollow-vs-solid. It is FILLED now
 *             — the outline was invisible on satellite — so that distinction is
 *             carried by COLOUR alone: no-contact keeps `--raad-marker-still` while
 *             parked/stale/unknown take `--raad-marker-parked`. Do not fold them together.)
 *
 * `stale` is a further VISUAL tone, not a motion: a fresh-fix motion whose cache
 * record has gone stale (the 30–120 s window) renders past tense — desaturated —
 * never as live. CLAUDE.md forbids showing stale data as if it were live, and the
 * map is the surface where that does the most harm.
 *
 * This object is the SINGLE source of DOM status-dot colour, so the theme slice
 * re-points every dot in one place. The dots keep six `--raad-motion-*` tones (with
 * a word beside each); the MAP collapsed to four in slice 56c and no longer uses a
 * per-state violet — `--raad-unknown` was removed with that collapse.
 */

/**
 * 🔴 SLICE 16 — THESE RESOLVE THROUGH THE PALETTE; THE LITERALS ARE FALLBACKS,
 * NOT THE DEFINITION. `styles.css` holds the values.
 *
 * 🔴 SLICE 23 — WAS A FROZEN `const`; NOW A FUNCTION. Read at MODULE IMPORT, the
 * result is captured once and a live theme toggle would leave every dot in the
 * old palette until reload. `getMotionColors()` re-resolves per call so a repaint
 * picks up the current theme. (The DOM dots do not even call this — they render
 * `MOTION_VAR` below, a `var(--raad-*)` string the CSS cascade re-points with no
 * JS at all; this function is the JS resolver for the module's tests and any
 * non-CSS consumer that needs a literal.)
 *
 * 🔴 THE LIGHT VALUES WERE RE-MEASURED, NOT CONVERTED (slice 23). Every
 * measurement here was taken at 8 px on `#0E1216`; the light ground invalidated
 * all of them at once. The light block re-ran the measurement on a rendered
 * contact sheet — parked / offline / unknown / stale stay separable at 8 px, in
 * daylight (min pairwise ΔE 13.4).
 */
export function getMotionColors() {
  return {
    moving: readToken('--raad-motion-moving', '#12B76A'),
    idle: readToken('--raad-motion-idle', '#F7B84B'),
    // D1 (slice 15b): parked is a distinct BLUE, not another grey — adopting the
    // mockup's #9AA7B4 would have redefined OFFLINE as parked on a client who
    // already learned the grey. Shipped semantics win.
    stopped: readToken('--raad-motion-stopped', '#44708F'),
    unknown: readToken('--raad-motion-unknown', '#A98AD8'),
    offline: readToken('--raad-motion-offline', '#9AA7B4'),
    stale: readToken('--raad-marker-stale', '#6B7683'),
    // The stale marker's RING — a touch lighter than its desaturated fill.
    staleStroke: readToken('--raad-marker-stale-stroke', '#8792a0'),
  } as const;
}

/**
 * The DOM status-dot colour per tone, as a CSS `var()` string — the theme-reactive
 * source every status dot renders (map sheet, list rows, detail Live tab, the
 * dashboard fleet preview). A string, not a resolved hex, so a theme flip repaints
 * the dots through the cascade with no re-render and no import-time freeze; and it
 * still carries no hex literal, so the single source of dot colour stays the
 * palette. `stale` reads `--raad-marker-stale`, which is not a `--raad-motion-*`
 * name — the stale tone is a marker neutral, not a motion.
 */
export const MOTION_VAR: Record<MarkerTone, string> = {
  moving: 'var(--raad-motion-moving)',
  idle: 'var(--raad-motion-idle)',
  stopped: 'var(--raad-motion-stopped)',
  unknown: 'var(--raad-motion-unknown)',
  offline: 'var(--raad-motion-offline)',
  stale: 'var(--raad-marker-stale)',
};

/** Beyond this age a fix is past tense, regardless of the last speed reading. */
export const STALE_AFTER_SEC = 120;

export type MarkerTone = 'moving' | 'idle' | 'stopped' | 'unknown' | 'offline' | 'stale';

/** A vehicle is on the map only when it has a real cached position. */
export function hasPosition(v: LiveVehicle): boolean {
  return v.lat != null && v.lon != null;
}

/**
 * Stale is the server's `stale` verdict, backed by the age. We trust the
 * server's flag first (it owns the clock); `positionAgeSec` is the fallback for
 * a response that predates the flag.
 */
export function isStale(v: LiveVehicle): boolean {
  if (v.stale) return true;
  return v.positionAgeSec != null && v.positionAgeSec > STALE_AFTER_SEC;
}

/**
 * The visual tone for a marker AND for the sheet pill. Offline keeps its own grey
 * even when also old — "not live" dominates. (It was a HOLLOW grey until 21 Sep;
 * the precedence here is unchanged, only the rendering is now filled.) `unknown` is resolved NEXT, before
 * the stale check: the API already decided the fix is too old to read (it emits
 * `motion:'unknown'` only past MOTION_FRESH_SEC), so this state owns its own tone
 * and must NOT collapse into the `stale`→`stopped` past-tense path — that silent
 * fall-through is exactly the defect this change closes. Otherwise a stale-but-
 * online fix (the 30–120 s window, still `motion:'moving'|'idle'|'stopped'`)
 * renders past tense; a fresh one renders its motion. A moving marker can never be
 * stale by construction, but the ordering here would still refuse to draw a live
 * green chevron on a stale fix if one ever arrived.
 */
export function markerTone(v: LiveVehicle): MarkerTone {
  if (v.motion === 'offline') return 'offline';
  if (v.motion === 'unknown') return 'unknown';
  if (isStale(v)) return 'stale';
  return v.motion;
}

/** Heading is drawn ONLY for a moving marker — course at 0 km/h is noise. */
export function showsHeading(tone: MarkerTone): boolean {
  return tone === 'moving';
}

/** Speed is shown ONLY when moving AND fresh — a stale speed is not current. */
export function showsSpeed(v: LiveVehicle): boolean {
  return markerTone(v) === 'moving';
}

/** Vehicles with a cached position but no marker would be silently lost — count them. */
export function countOffMap(vehicles: LiveVehicle[]): number {
  return vehicles.filter((v) => !hasPosition(v)).length;
}
